import { useCallback, useEffect, useMemo, useState } from "react";
import { api, normalizePaginatedList } from "../lib/api";
import { buildListParams, DEFAULT_LIST_PAGE_SIZE, EMPTY_LIST_META } from "../lib/listPagination";
import { useDataTable } from "./useDataTable";
import { LIST_FILTER_DEBOUNCE_MS, useDebouncedValue } from "./useDebouncedValue";

const EMPTY_EXTRA_PARAMS = {};

/**
 * Server-paginated list with column filters/sort (max 25 rows per page from API).
 *
 * @param {object} options
 * @param {string} options.path - API path e.g. "/purchases"
 * @param {import('../lib/dataTable').DataTableColumn[]} options.columns
 * @param {{ id: string, desc?: boolean } | null} [options.defaultSort]
 * @param {Record<string, string>} [options.extraParams] - fixed query params (state filter, etc.)
 * @param {boolean} [options.enabled=true]
 */
export function usePaginatedList({
  path,
  columns,
  defaultSort = null,
  extraParams = EMPTY_EXTRA_PARAMS,
  enabled = true,
  filterDebounceMs = LIST_FILTER_DEBOUNCE_MS,
}) {
  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState(EMPTY_LIST_META);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_LIST_PAGE_SIZE);
  const [loading, setLoading] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  const table = useDataTable(rows, columns, defaultSort, { serverSide: true });
  const appliedFilters = useDebouncedValue(table.columnFilters, filterDebounceMs);

  const extraKey = useMemo(() => JSON.stringify(extraParams), [extraParams]);
  const filterKey = useMemo(() => JSON.stringify(appliedFilters), [appliedFilters]);
  const sortKey = useMemo(() => JSON.stringify(table.sorting), [table.sorting]);

  useEffect(() => {
    setPage(1);
  }, [filterKey, sortKey, pageSize, extraKey]);

  const reload = useCallback(() => setRefreshKey((k) => k + 1), []);

  useEffect(() => {
    if (!enabled) return undefined;
    let cancelled = false;
    setLoading(true);
    const params = buildListParams(
      page,
      pageSize,
      appliedFilters,
      table.sorting,
      columns,
      extraParams,
    );
    api
      .get(path, { params })
      .then((r) => {
        if (cancelled) return;
        const body = normalizePaginatedList(r.data);
        setRows(body.results);
        setMeta({
          count: body.count,
          page: body.page,
          page_size: body.page_size,
          total_pages: body.total_pages,
          summary: body.summary,
        });
      })
      .catch(() => {
        if (!cancelled) {
          setRows([]);
          setMeta(EMPTY_LIST_META);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  // Depend on serialized keys only — extraParams/columns must not be object refs in deps
  }, [path, page, pageSize, filterKey, sortKey, extraKey, refreshKey, enabled]);

  const exportParams = useMemo(
    () => buildListParams(1, pageSize, appliedFilters, table.sorting, columns, extraParams),
    [filterKey, sortKey, extraKey, pageSize, columns, appliedFilters],
  );

  const pagination = useMemo(
    () => ({
      meta,
      pageSize,
      onPageSizeChange: setPageSize,
      onPageChange: setPage,
    }),
    [meta, pageSize],
  );

  return {
    table,
    rows,
    meta,
    loading,
    page,
    pageSize,
    setPage,
    setPageSize,
    pagination,
    reload,
    exportParams,
  };
}
